import PropTypes from 'prop-types';
import * as React from 'react';
import type ColumnSynchronizer from './ColumnSynchronizer';
import DataTableBodyRow from './DataTableBodyRow';
import type {ColumnPropType, RowIdPropType, RowPropType} from './propTypes';
import {columnPropType, columnSynchronizerPropType, rowIdPropType, rowPropType} from './propTypes';

export type Props = {
    /** Definice columns */
    columns: ColumnPropType,
    /** Synchronizace sloupců oddělené hlavičky a těla tabulky  */
    columnSynchronizer?: ColumnSynchronizer,
    /** Data - nutno mít v objektu ID - které bude number nebo string */
    rows: RowPropType[],
    /** Vybrená ID */
    selectedRowIds?: RowIdPropType,
    /** Klik na řádek */
    onRowClick?: (row: RowPropType) => void,
    /** Změna výberu */
    onSelectionChange?: (row: RowPropType, value: boolean, column: ColumnPropType) => void,
}

export default class DataTableBody extends React.PureComponent<Props> {

    static propTypes = {
        columns: PropTypes.arrayOf(columnPropType).isRequired,
        columnSynchronizer: columnSynchronizerPropType,
        rows: PropTypes.arrayOf(rowPropType).isRequired,
        selectedRowIds: PropTypes.arrayOf(rowIdPropType),
        rowProps: PropTypes.object,
        onRowClick: PropTypes.func,
        onRowSelectionChange: PropTypes.func,
    };

    static defaultProps = {
        selectedRowIds: [],
    };

    props: Props;
    body: React.DOMElement;

    componentDidMount(): void {
        this.startColumnSyncIfNeeded();
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.columnSynchronizer !== this.props.columnSynchronizer) {
            this.stopColumnSyncIfNeeded();
            this.startColumnSyncIfNeeded();
        }
    }

    componentWillUnmount(): void {
        this.stopColumnSyncIfNeeded();
    }

    startColumnSyncIfNeeded() {
        if (!this.columnSyncIntervalId && this.props.columnSynchronizer) {
            this.columnSyncIntervalId = setInterval(this.syncColumns, 1000);
        }
    }

    stopColumnSyncIfNeeded() {
        if (this.columnSyncIntervalId) {
            clearInterval(this.columnSyncIntervalId);
            this.columnSyncIntervalId = undefined;
        }
    }

    setBody = (body: React.DOMElement): void => {
        this.body = body;
    };

    syncColumns = (): void => {
        const {body, props: {columns, columnSynchronizer}} = this;
        if (body) {
            const rows = body.children;
            if (rows.length) {
                const cells = rows[0].children;
                for (let i = 0; i < cells.length; i++) {
                    const {name} = columns[i];
                    const {offsetWidth} = cells[i];
                    columnSynchronizer.setWidth(name, offsetWidth);
                }
            }
        }
    };

    renderRow = row => {
        const {columns, selectedRowIds, rowProps, onRowClick, onRowSelectionChange} = this.props;
        const selected = selectedRowIds.indexOf(row.id) >= 0;

        return <DataTableBodyRow {...rowProps}
                                 key={row.id}
                                 columns={columns}
                                 row={row}
                                 selected={selected}
                                 onClick={onRowClick}
                                 onSelectionChange={onRowSelectionChange}/>;
    };

    render(): React.ReactNode {
        const {
            columns,
            columnSynchronizer,
            rows,
            selectedRowIds,
            rowProps,
            onRowClick,
            onRowSelectionChange,
            ...otherProps
        } = this.props;

        return (
            <tbody {...otherProps} ref={this.setBody}>
            {rows.map(this.renderRow)}
            </tbody>
        );
    }

}
